import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarClock, CalendarRange, CheckCheck, CheckCircle2, ChevronDown, Circle, CircleAlert, Eye, FileText, Gauge, Megaphone, MessageSquareText,
  MessagesSquare, Send, ShieldCheck, UserPlus, UsersRound, Zap,
} from "lucide-react";
import axiosInstance from "src/common/axiosInstance";
import useChatChannel from "src/realtime/useChatChannel";
import { AuthContext } from "src/context/AuthContext";
import contactService from "src/features/Contact/services/contactService";
import templateService from "src/services/templateService";
import { WsDropdown } from "src/stage/WsControls";
import HelpTip from "src/stage/HelpTip";
import { CharacterStage, Kpi, Monogram, PageHeader, Skeleton, StagePage, Status, firstName, formatNumber, formatWhen, greetingFor, percent, relativeTime } from "src/stage/StageUi";
import { characterFor } from "src/stage/characters";
import useConnectedNumber from "src/stage/useConnectedNumber";
import { currentConnection } from "src/features/Broadcasts/broadcastApi";
import { BROADCAST_STATUS, progress, rate } from "src/features/Broadcasts/broadcastModel";
import MessagesChart from "./MessagesChart";
import { qualityOf, setupProgress, spotlight, tierLabel } from "./homeModel";
import "./dashboard.css";

// AUTO-WA-39 Home: a "Today" hub for people new to WhatsApp Business. It leads
// with the one thing to do next, then setup progress, customers waiting, the
// numbers, and the health of the WhatsApp number in plain words.
export const SCOPE_LABELS = {
  all: "Entire organisation",
  team: "Your reporting team",
  assigned: "Your assignments",
};

const RANGES = [
  { value: "7", label: "Last 7 days", short: "7 days" },
  { value: "30", label: "Last 30 days", short: "30 days" },
  { value: "90", label: "Last 90 days", short: "90 days" },
  { value: "all", label: "All time", short: "All time" },
];

const isoDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
export function rangeDates(value, today = new Date()) {
  if (value === "all") return { start_date: undefined, end_date: undefined };
  const start = new Date(today);
  start.setDate(start.getDate() - (Number(value) - 1));
  return { start_date: isoDay(start), end_date: isoDay(today) };
}

const titleCase = (name = "") => name.replace(/\b\w/g, (letter) => letter.toUpperCase());
const settle = (promise) => promise.then((value) => ({ ok: true, value }), (error) => ({ ok: false, status: error?.response?.status }));
const chatLink = (conversation, connection) => `/conversation?${new URLSearchParams({ conversation: conversation.id, workspace: connection?.workspaceId || "", number: connection?.phoneNumberId || "" })}`;

// Everything Home needs besides the dashboard figures. Each part fails alone.
function useHomeFacts(connection, version) {
  const [facts, setFacts] = useState({ loading: true });
  useEffect(() => {
    let active = true;
    if (!connection) { setFacts({ loading: false }); return undefined; }
    const { workspaceId, whatsappAccountId, phoneNumberId } = connection;
    Promise.all([
      settle(axiosInstance.get(`/phone-numbers/${phoneNumberId}/conversations`, { params: { filter: "unread", per_page: 5 } })),
      settle(axiosInstance.get(`/phone-numbers/${phoneNumberId}/conversations`, { params: { per_page: 1 } })),
      settle(contactService.index(workspaceId, whatsappAccountId, phoneNumberId, { per_page: 1 })),
      settle(templateService.forPhoneNumber(phoneNumberId, { status: "APPROVED" })),
      settle(axiosInstance.get(`/workspaces/${workspaceId}/ai-connection`)),
      settle(axiosInstance.get(`/phone-numbers/${phoneNumberId}/broadcasts`, { params: { per_page: 1 } })),
    ]).then(([waiting, chats, contacts, templates, ai, broadcasts]) => {
      if (!active) return;
      const total = (result) => (result.ok ? Number(result.value?.data?.meta?.total ?? result.value?.data?.pagination?.total ?? result.value?.data?.data?.length ?? 0) : null);
      setFacts({
        loading: false,
        waiting: waiting.ok ? { rows: waiting.value?.data?.data || [], total: total(waiting) } : null,
        conversations: total(chats),
        contacts: total(contacts),
        approvedTemplates: templates.ok ? (templates.value?.data?.data || []).length : null,
        botConnected: ai.ok ? Boolean(ai.value?.data?.data) : undefined,
        broadcasts: broadcasts.ok ? Number(broadcasts.value?.data?.counts?.all ?? broadcasts.value?.data?.pagination?.total ?? 0) : null,
        latestBroadcast: broadcasts.ok ? broadcasts.value?.data?.data?.[0] || null : undefined,
      });
    });
    return () => { active = false; };
  }, [connection?.workspaceId, connection?.phoneNumberId, connection?.whatsappAccountId, version]); // eslint-disable-line react-hooks/exhaustive-deps
  return facts;
}

function Spotlight({ spot, loading }) {
  const character = characterFor(spot.character);
  return (
    <section className="ws-spot wa-home-spot ws-rise" aria-label="What to do next">
      <div className="wa-home-spot-copy">
        <span className="ws-eyebrow">{spot.eyebrow}</span>
        {loading ? <Skeleton width={280} height={30} style={{ background: "rgba(255,255,255,.14)" }} /> : <h2>{spot.title}</h2>}
        <p className="ws-muted">{spot.text}</p>
        <Link className="ws-btn ws-btn--white" to={spot.to}>{spot.cta}<ArrowRight size={15} /></Link>
      </div>
      <div className="wa-home-spot-art" aria-hidden="true"><CharacterStage character={character} height={190} /></div>
    </section>
  );
}

function Waiting({ facts, connection }) {
  const rows = facts.waiting?.rows || [];
  return (
    <section className="ws-card wa-home-waiting" aria-label="Waiting for a reply">
      <div className="wa-card-head">
        <div className="ws-grow"><h2 className="ws-h3">Waiting for a reply</h2><p className="ws-muted">Unread chats, newest first.</p></div>
        <Link className="ws-btn ws-btn--quiet ws-btn--sm" to="/conversation?filter=unread">All unread<ArrowRight size={14} /></Link>
      </div>
      <ul className="wa-list">
        {facts.loading && [0, 1, 2].map((i) => <li key={i} style={{ padding: "8px 12px" }}><Skeleton height={36} /></li>)}
        {!facts.loading && !facts.waiting && <li className="ws-empty" style={{ margin: "0 8px 8px" }}><strong>Chats unavailable</strong>Open Chats to see your messages.</li>}
        {!facts.loading && facts.waiting && rows.length === 0 && (
          <li className="ws-empty wa-home-clear" style={{ margin: "0 8px 8px" }}><CheckCircle2 size={22} color="#1B7A5A" /><strong>You’re all caught up</strong>New messages from customers appear here.</li>
        )}
        {rows.map((conversation) => {
          const name = conversation.contact?.name ? titleCase(conversation.contact.name) : conversation.contact?.phone_number || "Unknown";
          return (
            <li key={conversation.id} className="wa-list-item">
              <Link to={chatLink(conversation, connection)} className="wa-list-link wa-home-chat">
                <Monogram name={name} size={34} />
                <span className="ws-grow" style={{ minWidth: 0 }}>
                  <strong className="wa-ellipsis">{name}</strong>
                  <span className="ws-muted wa-ellipsis">{conversation.last_message?.preview || "New message"}</span>
                </span>
                <span className="wa-home-chat-meta">
                  <span className="ws-muted">{relativeTime(conversation.last_message_at || conversation.last_message?.meta_timestamp)}</span>
                  {conversation.unread_count > 0 && <span className="wa-unread ws-num">{conversation.unread_count}</span>}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function SetupChecklist({ progress: setup }) {
  const [open, setOpen] = useState(true);
  if (!setup.total || setup.finished) return null;
  const pct = Math.round((setup.completed / setup.total) * 100);
  return (
    <section className="ws-card wa-setup ws-rise" aria-label="Getting started">
      <button type="button" className="wa-setup-head" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span className="wa-setup-ring" style={{ "--pct": `${pct}%` }} aria-hidden="true"><span className="ws-num">{setup.completed}/{setup.total}</span></span>
        <span className="ws-grow">
          <strong>Get set up</strong>
          <span className="ws-muted">{setup.total - setup.completed} {setup.total - setup.completed === 1 ? "step" : "steps"} left. Each takes a few minutes.</span>
        </span>
        <ChevronDown size={18} className={`wa-setup-chev${open ? " is-open" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <ol className="wa-setup-steps">
          {setup.steps.map((step) => (
            <li key={step.id} className={step.done ? "is-done" : step === setup.next ? "is-next" : ""}>
              {step.done ? <CheckCircle2 size={20} className="wa-setup-icon is-done" aria-label="Done" /> : <Circle size={20} className="wa-setup-icon" aria-label="Not done yet" />}
              <span className="ws-grow">
                <strong>{step.title}</strong>
                {!step.done && <span className="ws-muted">{step.why}</span>}
              </span>
              {!step.done && <Link className={`ws-btn ws-btn--sm ${step === setup.next ? "ws-btn--primary" : "ws-btn--quiet"}`} to={step.to}>{step.cta}</Link>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function DeliveryCard({ delivery, loading }) {
  const sent = delivery?.sent || 0;
  const rows = [
    { key: "sent", label: "Sent", icon: Send, value: sent, pct: percent(sent, delivery?.outgoing) },
    { key: "delivered", label: "Delivered", icon: CheckCheck, value: delivery?.delivered, pct: percent(delivery?.delivered, sent) },
    { key: "read", label: "Read", icon: Eye, value: delivery?.read, pct: percent(delivery?.read, sent) },
  ];
  return (
    <section className="ws-card ws-card-pad wa-home-delivery" aria-label="Delivery">
      <div className="ws-card-head"><h2 className="ws-h3">Delivery</h2><span className="ws-muted">of messages you sent</span></div>
      {loading ? <div style={{ display: "grid", gap: 14, marginTop: 10 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={34} />)}</div> : (
        <div className="wa-home-bars">
          {rows.map(({ key, label, icon: Icon, value, pct }) => (
            <div key={key} className="wa-home-bar">
              <span className="wa-home-bar-label"><Icon size={14} aria-hidden="true" />{label}</span>
              <span className="ws-num wa-home-bar-value">{formatNumber(value || 0)} <small>{sent ? `${pct}%` : ""}</small></span>
              <span className="ws-meter" aria-hidden="true"><span style={{ width: `${sent ? pct : 0}%` }} /></span>
            </div>
          ))}
          <div className="wa-home-failed">
            {delivery?.failed ? <Status tone="failed">{formatNumber(delivery.failed)} failed</Status> : <Status tone="live">No failed messages</Status>}
          </div>
        </div>
      )}
    </section>
  );
}

function NumberHealth({ number }) {
  const quality = qualityOf(number?.quality_rating);
  const tier = tierLabel(number?.messaging_limit_tier);
  return (
    <section className="ws-card ws-card-pad wa-home-health" aria-label="Your WhatsApp number">
      <div className="ws-card-head"><h2 className="ws-h3 ws-grow">Your WhatsApp number</h2><Link className="ws-btn ws-btn--plain ws-btn--sm" to="/settings/connection">Settings</Link></div>
      {!number ? <Skeleton height={80} radius={12} /> : (
        <>
          <div className="wa-home-number">
            <span className="wa-home-number-icon" aria-hidden="true"><MessagesSquare size={18} /></span>
            <span className="ws-grow" style={{ minWidth: 0 }}>
              <strong className="wa-ellipsis">{number.verified_name || number.display_name || "Your business"}</strong>
              <span className="ws-muted">{number.phone_number}</span>
            </span>
            <Status tone={number.status === "Connected" ? "live" : "warning"}>{number.status === "Connected" ? "Connected" : number.status || "Unknown"}</Status>
          </div>
          <dl className="wa-home-facts">
            <div>
              <dt><ShieldCheck size={14} aria-hidden="true" />Quality <HelpTip term="quality" /></dt>
              <dd><Status tone={quality.tone}>{quality.label}</Status></dd>
              <p className="ws-muted">{quality.text}</p>
            </div>
            <div>
              <dt><Gauge size={14} aria-hidden="true" />Daily limit <HelpTip term="tier" align="right" /></dt>
              <dd className="ws-num">{tier ? (tier === "Unlimited" ? "Unlimited" : `${tier} people`) : "Not set yet"}</dd>
              <p className="ws-muted">New conversations your business can start each day.</p>
            </div>
          </dl>
        </>
      )}
    </section>
  );
}

function LatestBroadcast({ broadcast }) {
  if (!broadcast) {
    return (
      <section className="ws-card ws-card-pad wa-home-bc">
        <span className="ws-icon-tile" aria-hidden="true"><Megaphone size={18} /></span>
        <h2 className="ws-h3" style={{ marginTop: 10 }}>Reach many customers at once <HelpTip term="broadcast" /></h2>
        <p className="ws-muted" style={{ marginTop: 4 }}>Send an approved template to everyone, or to people with a tag.</p>
        <Link className="ws-btn ws-btn--primary ws-btn--sm" style={{ marginTop: 12, alignSelf: "flex-start" }} to="/broadcast/new">New broadcast</Link>
      </section>
    );
  }
  const meta = BROADCAST_STATUS[broadcast.status] || BROADCAST_STATUS.completed;
  const stats = broadcast.stats || {};
  return (
    <Link to={`/broadcast/${broadcast.id}`} className="ws-card ws-card-pad wa-home-bc is-link">
      <div className="ws-card-head"><span className="ws-eyebrow">Latest broadcast</span></div>
      <strong className="wa-ellipsis" style={{ fontSize: 15 }}>{broadcast.name}</strong>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}><Status tone={meta.tone}>{meta.label}</Status><span className="ws-muted">{formatNumber(broadcast.total_recipients)} people</span></div>
      {broadcast.status === "sending" && <span className="ws-meter" style={{ marginTop: 10 }}><span style={{ width: `${progress(stats)}%` }} /></span>}
      {broadcast.status === "scheduled" && (
        <p className="wa-home-bc-when"><CalendarClock size={14} aria-hidden="true" />Sends {formatWhen(broadcast.scheduled_at)}</p>
      )}
      {broadcast.status !== "scheduled" && (
        <div className="wa-bc-mini" style={{ marginTop: 12 }}>
          <span><strong className="ws-num">{formatNumber(stats.sent || 0)}</strong><small>Sent</small></span>
          <span><strong className="ws-num">{rate(stats.read, stats.sent) ?? "—"}{stats.sent ? "%" : ""}</strong><small>Read</small></span>
          <span><strong className="ws-num">{formatNumber(stats.replied || 0)}</strong><small>Replied</small></span>
        </div>
      )}
    </Link>
  );
}

const QUICK = [
  { to: "/broadcast/new", icon: Megaphone, title: "New broadcast", text: "Message many people" },
  { to: "/contacts", icon: UserPlus, title: "Add contacts", text: "Import or add people" },
  { to: "/templates?examples=1", icon: FileText, title: "New template", text: "Start from an example" },
  { to: "/settings/quick-replies", icon: Zap, title: "Quick replies", text: "Save answers you repeat" },
];

function Dashboard() {
  const { currentUser } = useContext(AuthContext);
  const connection = currentConnection();
  const phoneNumberId = localStorage.getItem("phoneNumberId");
  const [range, setRange] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  const sequence = useRef(0);
  const dates = useMemo(() => rangeDates(range), [range]);
  const facts = useHomeFacts(connection, version);
  const number = useConnectedNumber(connection?.phoneNumberId);

  const refresh = useCallback(async () => {
    const current = ++sequence.current;
    if (!phoneNumberId) {
      setData(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axiosInstance.get(`/phone-numbers/${phoneNumberId}/dashboard`, { params: dates });
      if (current !== sequence.current) return;
      setData(response.data.data);
      setError(null);
    } catch {
      if (current !== sequence.current) return;
      // Never keep figures or customers from before a permission change.
      setData(null);
      setError("Your dashboard couldn’t be loaded. Try again.");
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }, [phoneNumberId, dates]);

  const cancelPending = useCallback(() => { sequence.current++; }, []);
  useEffect(() => {
    setLoading(true);
    setData(null);
    refresh();
    return cancelPending;
  }, [refresh, cancelPending]);
  const onLive = useCallback(() => { refresh(); setVersion((value) => value + 1); }, [refresh]);
  useChatChannel(phoneNumberId, { onInvalidate: onLive, onReconnect: onLive });

  const setup = setupProgress({ connected: Boolean(connection), ...facts });
  const spot = spotlight({ waiting: facts.waiting?.total ?? null, progress: setup });
  const stats = data?.stats;
  const name = firstName(currentUser?.name);
  const showSkeleton = loading && !data;
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });

  return (
    <StagePage className="wa-home" label="Home">
      <PageHeader
        eyebrow={`${today}${data?.scope ? ` · ${SCOPE_LABELS[data.scope]}` : ""}`}
        title={`${greetingFor()}${name ? `, ${name}` : ""}`}
        description="Here’s what needs you on WhatsApp today."
        actions={(
          <>
            <Link className="ws-btn ws-btn--quiet" to="/conversation"><MessagesSquare size={15} />Open Chats</Link>
            <Link className="ws-btn ws-btn--primary" to="/broadcast/new"><Megaphone size={15} />New broadcast</Link>
          </>
        )}
      />

      {error && (
        <div className="wa-notice wa-notice--bad" role="alert">
          <CircleAlert size={16} /><span className="ws-grow">{error}</span>
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={refresh}>Try again</button>
        </div>
      )}

      <div className="wa-home-top">
        <Spotlight spot={spot} loading={facts.loading} />
        <Waiting facts={facts} connection={connection} />
      </div>

      {!facts.loading && <SetupChecklist progress={setup} />}

      <div className="wa-home-section-head">
        <h2 className="ws-h2">Your numbers</h2>
        <WsDropdown label="Period" icon={CalendarRange} value={range} options={RANGES} onChange={setRange} align="right" />
      </div>
      <section className="ws-card ws-kpis" aria-label="Summary">
        <Kpi icon={MessagesSquare} label="Conversations" loading={showSkeleton} value={formatNumber(stats?.totalConversations)} />
        <Kpi icon={UsersRound} label="New contacts" loading={showSkeleton} value={formatNumber(stats?.newContacts)} />
        <Kpi icon={Send} label="Messages sent" loading={showSkeleton} value={formatNumber(stats?.messagesSent)} />
        <Kpi icon={CheckCheck} label="Delivered" loading={showSkeleton}
          value={stats?.messagesSent ? <>{((stats.messagesDelivered / stats.messagesSent) * 100).toFixed(1)}<small>%</small></> : "—"}
          hint={stats ? `${formatNumber(stats.messagesDelivered)} messages` : null} />
      </section>

      <div className="wa-home-row">
        <section className="ws-card ws-card-pad wa-home-chart" aria-label="Messages per day">
          <div className="ws-card-head"><h2 className="ws-h3">Messages per day</h2><span className="ws-muted">{RANGES.find((item) => item.value === range)?.label}</span></div>
          {showSkeleton ? <Skeleton height={260} radius={14} /> : <MessagesChart points={data?.chart || []} />}
        </section>
        <DeliveryCard delivery={data?.delivery} loading={showSkeleton} />
      </div>

      <div className="wa-home-bottom">
        <NumberHealth number={number} />
        {facts.latestBroadcast !== undefined && <LatestBroadcast broadcast={facts.latestBroadcast} />}
        <nav className="wa-home-quick" aria-label="Quick actions">
          {QUICK.map(({ to, icon: Icon, title, text }) => (
            <Link key={to} to={to} className="ws-link-tile"><Icon size={17} aria-hidden="true" /><span><strong>{title}</strong><span className="ws-muted">{text}</span></span></Link>
          ))}
        </nav>
      </div>
      <p className="wa-help wa-home-scope"><MessageSquareText size={13} aria-hidden="true" />
        {data?.scope === "all"
          ? "You see all customer work in this organisation."
          : data?.scope === "team"
            ? "You see work assigned to you and the people who report to you."
            : "You see only contacts and chats assigned to you."}
      </p>
    </StagePage>
  );
}

export default Dashboard;
